import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from '@mui/material';
import {
  useCampaignsQuery,
  useDeleteCampaignMutation,
  useUpdateCampaignStatusMutation,
} from '../hooks/api';
import { useSnackbar } from '../hooks';
import { Campaign } from '../types';
import { LoadingIndicator, ErrorDisplay, ConfirmationDialog } from './common';
import CampaignListItem from './CampaignListItem';
import { useAuth } from '../contexts/AuthContext';

const CampaignList: React.FC = () => {
  // Use React Query hooks
  const { data: campaigns = [], isLoading: loading, error, refetch } = useCampaignsQuery();
  const deleteMutation = useDeleteCampaignMutation();
  const updateStatusMutation = useUpdateCampaignStatusMutation();

  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const navigate = useNavigate();
  const { canAddCampaigns } = useAuth();

  const handleDelete = (campaign: Campaign): void => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!campaignToDelete) return;

    try {
      await deleteMutation.mutateAsync(campaignToDelete.campaign_id);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      showSnackbar('Campaign deleted successfully', 'success');
    } catch (err) {
      showSnackbar(`Failed to delete campaign: ${(err as Error).message}`, 'error');
    }
  };

  const toggleCampaignStatus = async (
    campaignId: string,
    newStatus: 'enabled' | 'disabled'
  ): Promise<void> => {
    try {
      await updateStatusMutation.mutateAsync({ campaignId, status: newStatus });
      showSnackbar(`Campaign ${newStatus} successfully`, 'success');
    } catch (err) {
      showSnackbar(`Failed to update campaign status: ${(err as Error).message}`, 'error');
    }
  };

  if (loading) {
    return <LoadingIndicator message="Loading campaigns..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error.message || 'Failed to load campaigns'}
        retryAction={refetch}
        backRoute="/"
        backLabel="Go to Dashboard"
      />
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Campaign List
        </Typography>
        {canAddCampaigns() && (
          <Button variant="contained" color="primary" onClick={() => navigate('/new-campaign')}>
            Create New Campaign
          </Button>
        )}
      </Box>

      {campaigns.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No campaigns found</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Create a new campaign to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="campaign table">
            <TableHead>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                {/* Remove headers for stats no longer in this view */}
                {/* <TableCell>Data Collected (seconds)</TableCell>
                <TableCell>Last Updated</TableCell> */}
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map(campaign => (
                <CampaignListItem
                  key={campaign.campaign_id}
                  campaign={campaign}
                  // Pass toggleCampaignStatus from the hook
                  onToggleStatus={toggleCampaignStatus}
                  onDelete={handleDelete}
                  onViewDetails={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the campaign "${campaignToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <SnackbarComponent />
    </div>
  );
};

export default CampaignList;
